import Modal from "@via/components/modal";
import { ModalSize } from "@via/utils/constants";
import { PermissionType } from "common";
import { FC, useMemo } from "react";

interface ConfirmPermissionChangeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;

  // Current state
  currentType: PermissionType;
  currentGroupId?: number | null;
  currentGroupLabel?: string | null;

  // Next state being requested
  nextType: PermissionType;
  nextGroupLabel?: string | null;
  nextGroupId?: number | null;

  // Loading + submit
  isConfirmLoading?: boolean;
  onConfirm: () => void; // you call updateProject inside the parent on confirm
  onCancel?: () => void;
  resource: string;
}

const readable = (t: PermissionType) => {
  switch (t) {
    case PermissionType.Public:
      return "Public access";
    case PermissionType.GroupShared:
      return "Share with a group";
    case PermissionType.SpecificUsersShared:
      return "Share with specific users";
    case PermissionType.UserOwned:
    default:
      return "Private to you";
  }
};

const makeWarningText = (
  currentType: PermissionType,
  nextType: PermissionType,
  resource: string,
  currentGroupId?: number | null,
  currentGroupLabel?: string | null,
  nextGroupLabel?: string | null,
  nextGroupId?: number | null
) => {
  // Changing within Group -> Group (potential replacement)
  if (
    currentType === PermissionType.GroupShared &&
    nextType === PermissionType.GroupShared
  ) {
    if (currentGroupId && nextGroupId && currentGroupId !== nextGroupId) {
      if (currentGroupLabel) {
        return `You're about to change the shared group from “${currentGroupLabel}” to “${nextGroupLabel}”. Members of “${currentGroupLabel}” will lose access, and “${nextGroupLabel}” will gain access. Continue?`;
      } else {
        return `You're about to change the shared group. Members of the current group will lose access, and members of “${nextGroupLabel}” will gain access. Continue?`;
      }
    }
    // same group selected again — very rare, keep a generic message
    return `This ${resource} is already shared with the selected group.`;
  }

  // Leaving Group to something else
  if (
    currentType === PermissionType.GroupShared &&
    nextType !== PermissionType.GroupShared
  ) {
    if (currentGroupLabel) {
      return `This will remove group access${currentGroupLabel ? ` for “${currentGroupLabel}”` : ""}. Users in that group will lose access. Continue?`;
    } else {
      return `This will remove group access. Users in that group will lose access. Continue?`;
    }
  }

  // Going TO Group from anything else
  if (
    currentType !== PermissionType.GroupShared &&
    nextType === PermissionType.GroupShared
  ) {
    return `This ${resource} will be shared with the group${nextGroupLabel ? ` “${nextGroupLabel}”` : ""}. People in that group will gain access. Continue?`;
  }

  // Going public
  if (
    nextType === PermissionType.Public &&
    currentType !== PermissionType.Public
  ) {
    return `This will make the ${resource} visible to anyone. Continue?`;
  }

  // Going to specific users
  if (
    nextType === PermissionType.SpecificUsersShared &&
    currentType !== PermissionType.SpecificUsersShared
  ) {
    return "Group or public access (if any) will be revoked. Only specifically added users will retain access. Continue?";
  }

  // Going private
  if (
    nextType === PermissionType.UserOwned &&
    currentType !== PermissionType.UserOwned
  ) {
    return `All shared access will be removed. Only you will be able to access this ${resource}. Continue?`;
  }

  // Fallback generic
  return `Change permission from “${readable(currentType)}” to “${readable(nextType)}”?`;
};

const ConfirmPermissionChangeModal: FC<ConfirmPermissionChangeModalProps> = ({
  open,
  setOpen,
  currentType,
  currentGroupId,
  currentGroupLabel,
  nextType,
  nextGroupLabel,
  nextGroupId,
  isConfirmLoading = false,
  onConfirm,
  onCancel,
  resource,
}) => {
  const warningText = useMemo(
    () =>
      makeWarningText(
        currentType,
        nextType,
        resource,
        currentGroupId,
        currentGroupLabel,
        nextGroupLabel,
        nextGroupId
      ),
    [
      currentType,
      nextType,
      resource,
      currentGroupId,
      currentGroupLabel,
      nextGroupLabel,
      nextGroupId,
    ]
  );

  const handleConfirm = async (): Promise<void> => {
    await onConfirm();
  };

  const handleCancel = () => {
    onCancel?.();
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Confirm Permission Change"
      confirmText="Confirm"
      size={ModalSize.Large}
      onCancel={handleCancel}
      isConfirmLoading={isConfirmLoading}
      onConfirm={handleConfirm}>
      <form>
        <div className="mt-0">
          <div className="mb-4 text-sm text-gray-600">
            {warningText}
            {resource === "project" && (
              <p className="py-2">This change takes effect immediately.</p>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ConfirmPermissionChangeModal;
